/* Built In Imports */
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';

/* External Imports */
import { Box } from '@chakra-ui/react';

/* Internal Imports */
/* Components */
import Breadcrum from '@components/Navigation/Breadcrum';
import CenterCommonUI from '@components/UI/Center';
import LeftNavCommonUI from '@components/UI/Common/LeftNavCommonUI';
import HeadComponent from '@components/Utility/HeadComponent';
import config from '@config';
import WrapLayout from '@Layout/WrapLayout';
import { AuthContext } from '@store/auth-context';

/* Services */
import { getLocale } from '@components/Utility/Shared/SharedService';
import {
  fetchFooterData,
  fetchHeaderMenuData,
  fetchHeaderTopMenuData,
} from 'services/commonService';
import { fetchDetails, getLandingPaths } from 'services/donationService';

/* Styles */

/**
 * @param {string} region - Current User Region
 * @param {string} language - Current User Language
 * @param {string} pageData - Health Landing Page Data
 */
const DonateInnerCategory = ({ region, language, pageData, innerCategory }) => {
  const router = useRouter();
  const authContext = useContext(AuthContext);

  useEffect(() => {
    authContext.RegionAndLang(region, language);
  }, [authContext, region, language]);

  // consoleLog('page', pageData);

  return (
    <WrapLayout pad={0}>
      {router.asPath.indexOf('donation/contribute/shivanga-sadhana') > -1 && (
        <Head>
          <meta name="robots" content="noindex" />
        </Head>
      )}
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData.availableLocales}
      >
        {' '}
      </HeadComponent>
      {innerCategory !== 'shivanga-sadhana' ||
        ('maha-annadanam' && (
          <Breadcrum
            page={pageData.breadcrumbs}
            alsoin={pageData.alsoin}
            url=""
            region={region}
            lang={language}
            pageConfig={pageData?.pageConfig}
          />
        ))}
      <Box
        maxW={{
          base: '100%',
          lg:
            innerCategory === 'shivanga-sadhana' || 'maha-annadanam'
              ? 1920
              : 1170,
        }}
        mx="auto"
      >
        <CenterCommonUI
          pageBody={pageData.body}
          region={region}
          lang={language}
        />
        <LeftNavCommonUI
          pageBody={pageData.bodyLeftNavSections}
          region={region}
          lang={language}
          isLeftMenu
        />
        <CenterCommonUI
          pageBody={pageData.bodyBottomSection}
          region={region}
          lang={language}
        />
      </Box>
    </WrapLayout>
  );
};

export const getStaticPaths = async () => {
  const donationCategoryPaths = config.USE_LOCAL_URL
    ? [
        `/in/en/donation/recurring-donations/faq`,
        `/in/en/donation/contribute/shivanga-sadhana`,
        `/us/en/donation/contribute/shivanga-sadhana`,
        `/uk/en/donation/contribute/shivanga-sadhana`,
        `/in/en/donation/projects/shivanga-sadhana`,
        `/in/en/donation/initiatives/shivanga-sadhana`,
        `/in/en/donation/build/shivanga-sadhana`,
        `/in/en/donation/serve/shivanga-sadhana`,
        `/in/en/donation/act/shivanga-sadhana`,
        `/in/en/donation/impact/shivanga-sadhana`,
        `/in/en/donation/contribute/maha-annadanam`,
      ]
    : await getLandingPaths('L2', '/donation/', null, 'others_section');
  // consoleLog('donationCategoryPaths', donationCategoryPaths);
  return {
    paths: donationCategoryPaths,
    fallback: false,
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, language, category, innerCategory },
  req,
}) => {
  const headerTopMenuData = await fetchHeaderTopMenuData(
    getLocale(region, language),
    'isotopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale(region, language),
    'isomainmenu'
  );
  const footerData = await fetchFooterData(
    getLocale(region, language),
    'isofooter'
  );
  const pageData = await fetchDetails(
    region,
    language,
    `${category}/${innerCategory}`,
    { preview, data: previewData }
  );

  return {
    props: {
      headerTopMenuData: headerTopMenuData?.data || null,
      headerMenuData: headerMenuData?.data || null,
      region: region,
      language: language,
      footerData: footerData?.data || null,
      pageData: pageData || null,
      isPreview: preview ? true : false,
      innerCategory: innerCategory,
      headerVisible:
        innerCategory === 'shivanga-sadhana' || 'maha-annadanam' ? false : true,
    },
  };
};

export default DonateInnerCategory;
